import React, { useEffect, useMemo, useRef, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

function isLikelyYear(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return false;
  }
  const absoluteValue = Math.abs(numericValue);
  return Number.isInteger(numericValue) && absoluteValue >= 1000 && absoluteValue <= 9999;
}

function formatNumericValue(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '—';
  }
  const numericValue = Number(value);
  if (isLikelyYear(numericValue)) {
    return String(numericValue);
  }
  return numericValue.toLocaleString();
}

function formatDifference(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '—';
  }
  return formatNumericValue(Math.abs(Number(value)));
}

function formatTime(milliseconds) {
  if (milliseconds === null || milliseconds === undefined || !Number.isFinite(Number(milliseconds))) {
    return null;
  }
  return `${(Number(milliseconds) / 1000).toFixed(2)}s`;
}

export function getOrderedRevealRows(roundResults = []) {
  const rows = Array.isArray(roundResults) ? roundResults.slice() : [];
  const answered = rows.filter((row) => row?.hadAnswer).sort((left, right) => {
    const leftDiff = Number(left?.diff ?? Number.POSITIVE_INFINITY);
    const rightDiff = Number(right?.diff ?? Number.POSITIVE_INFINITY);
    if (leftDiff !== rightDiff) return rightDiff - leftDiff;

    const leftTime = Number(left?.answerTime ?? Number.POSITIVE_INFINITY);
    const rightTime = Number(right?.answerTime ?? Number.POSITIVE_INFINITY);
    if (leftTime !== rightTime) return leftTime - rightTime;

    return String(left?.username || '').localeCompare(String(right?.username || ''));
  });
  const unanswered = rows.filter((row) => !row?.hadAnswer);
  return [...answered, ...unanswered];
}

export default function ClosestGuessGame({ gameState = {}, gamePlayers = [], user = {}, socket, roomId, onGameAction }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const [guess, setGuess] = useState('');
  const [timeLeft, setTimeLeft] = useState({ value: gameState.timeLimit || 15, round: -1 });
  const [revealedRows, setRevealedRows] = useState([]);
  const frameRef = useRef(null);

  const state = useMemo(() => gameState || {}, [gameState]);
  const currentQuestion = state.currentQuestion || null;
  const phase = state.phase || 'question';
  const roundIndex = Number.isFinite(Number(state.roundIndex)) ? Number(state.roundIndex) : 0;
  const rounds = Number.isFinite(Number(state.rounds)) ? Number(state.rounds) : 0;
  const timeLimit = Number.isFinite(Number(state.timeLimit)) ? Number(state.timeLimit) : 15;
  const finished = !!state.finished;
  const meId = user ? (user.socketId || user.userId) : null;
  const submittedCount = Number.isFinite(Number(state.submittedCount)) ? Number(state.submittedCount) : 0;
  const playerCount = Array.isArray(gamePlayers) ? gamePlayers.length : 0;
  const mySubmitted = state.answers && meId !== null && Object.prototype.hasOwnProperty.call(state.answers, meId);

  const dispatchAction = async (action) => {
    if (typeof onGameAction === 'function') {
      return onGameAction(action);
    }
    return submitAction(action);
  };

  useEffect(() => {
    setGuess('');
  }, [roundIndex]);

  useEffect(() => {
    setTimeLeft({ value: timeLimit, round: roundIndex });
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }
    if (phase !== 'question' || finished) return undefined;

    const start = Date.now();
    const tick = () => {
      const elapsed = (Date.now() - start) / 1000;
      setTimeLeft({ value: Math.max(0, Math.ceil(timeLimit - elapsed)), round: roundIndex });
      if (elapsed < timeLimit && phase === 'question' && !finished) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [phase, finished, roundIndex, timeLimit]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (finished || phase !== 'question' || mySubmitted) return;
    const numericGuess = Number.parseFloat(guess);
    if (!Number.isFinite(numericGuess)) return;
    setGuess('');
    await dispatchAction({ type: 'submit-guess', guess: numericGuess });
  };

  const rankings = Array.isArray(state.rankings) ? state.rankings : [];
  const revealRows = useMemo(() => (Array.isArray(state.roundResults) ? state.roundResults : []), [state.roundResults]);
  const orderedRevealRows = useMemo(() => getOrderedRevealRows(revealRows), [revealRows]);
  const displayRevealRows = phase === 'reveal' && playerCount > 1 ? revealedRows : orderedRevealRows;

  useEffect(() => {
    if (phase !== 'reveal' || finished) {
      setRevealedRows([]);
      return undefined;
    }

    const orderedRows = getOrderedRevealRows(revealRows);
    if (orderedRows.length <= 1 || playerCount <= 1) {
      setRevealedRows(orderedRows);
      return undefined;
    }

    setRevealedRows(orderedRows.slice(0, 1));
    const revealStepMs = Math.max(700, Math.round((Number(state.reviewAnswerTime || timeLimit || 8) * 1000) / Math.max(1, orderedRows.length)));
    let nextIndex = 1;
    const intervalId = window.setInterval(() => {
      setRevealedRows((currentRows) => {
        if (nextIndex >= orderedRows.length) {
          window.clearInterval(intervalId);
          return orderedRows;
        }
        const nextRows = orderedRows.slice(0, nextIndex + 1);
        nextIndex += 1;
        return nextRows;
      });
    }, revealStepMs);

    return () => window.clearInterval(intervalId);
  }, [phase, finished, revealRows, playerCount, state.reviewAnswerTime, timeLimit]);

  return (
    <div className="w-full rounded-[28px] border border-slate-200 bg-white p-4 text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap gap-2 text-xs font-semibold uppercase tracking-[0.12em]">
          <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700 dark:bg-slate-800 dark:text-slate-200">Round {Math.min(roundIndex + 1, rounds || 0)} / {rounds || 0}</span>
          <span className="rounded-full bg-sky-100 px-3 py-1 text-sky-700 dark:bg-sky-900/50 dark:text-sky-200">{phase}</span>
          <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-700 dark:bg-amber-900/50 dark:text-amber-200">{submittedCount}/{playerCount || 0} submitted</span>
        </div>
      </div>

      <div className="mb-4 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">Question</div>
        <div className="mt-2 text-xl font-semibold leading-8 text-slate-900 dark:text-slate-100">{currentQuestion ? currentQuestion.prompt : 'Waiting for the next question...'}</div>
        <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">{currentQuestion && currentQuestion.unit ? `Answer in ${currentQuestion.unit}.` : 'Submit a number as your guess.'}</div>

        {phase === 'question' && !finished ? (
          <>
            <form className="mt-4 flex flex-col gap-2 sm:flex-row" onSubmit={handleSubmit}>
              <input
                type="number"
                inputMode="decimal"
                step="any"
                value={guess}
                onChange={(event) => setGuess(event.target.value)}
                disabled={finished || mySubmitted}
                placeholder="Enter your guess"
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 outline-none transition focus:border-emerald-500 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
              />
              <button
                type="submit"
                disabled={finished || mySubmitted || guess === ''}
                className="rounded-2xl bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {mySubmitted ? 'Submitted' : 'Lock In'}
              </button>
            </form>

            <div className="mt-4 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
              <div className="h-3 bg-emerald-500 transition-[width] duration-100" style={{ width: `${(1 - (timeLeft.value / timeLimit)) * 100}%` }} />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
              <span>{timeLeft.value}s left</span>
              <span>Guesses stay hidden until reveal.</span>
            </div>

            {mySubmitted ? (
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100">Your guess is in. Waiting for the rest of the room.</div>
            ) : null}
          </>
        ) : null}

        {phase === 'reveal' && !finished ? (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900/80">
            <div className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Reveal</div>
            <div className="mt-2 text-lg font-bold text-emerald-700 dark:text-emerald-400">Correct answer: {Number.isFinite(Number(currentQuestion?.answer)) ? formatNumericValue(currentQuestion.answer) : '—'}{currentQuestion?.unit ? ` ${currentQuestion.unit}` : ''}</div>
            {currentQuestion?.explanation ? <div className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{currentQuestion.explanation}</div> : null}

            <div className="mt-4 overflow-x-auto">
              {phase === 'reveal' && playerCount > 1 && orderedRevealRows.length > 1 ? (
                <div className="mb-3 flex items-center justify-between text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  <span>Revealing guesses</span>
                  <span>{displayRevealRows.length}/{orderedRevealRows.length}</span>
                </div>
              ) : null}
              <div className="space-y-2">
                {displayRevealRows.map((row) => {
                  const isExactMatch = row?.hadAnswer && Number(row?.diff ?? 0) === 0;

                  return (
                  <div key={row.playerId} className={`rounded-2xl px-3 py-3 ${isExactMatch ? 'border border-emerald-500/60 bg-emerald-50/80 dark:border-emerald-400/50 dark:bg-emerald-500/10' : 'bg-slate-50 dark:bg-slate-800/70'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate font-semibold">{row.username}</div>
                        <div className="mt-1 text-xs uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                          {row.hadAnswer ? 'Guess' : 'No answer'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-slate-900 dark:text-slate-100">{row.hadAnswer ? formatNumericValue(row.guess) : '—'}</div>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">+{row.award || 0} pts</div>
                      </div>
                    </div>
                    <div className={`mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm ${isExactMatch ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-600 dark:text-slate-300'}`}>
                      <span className="tabular-nums">Diff: {row.hadAnswer ? formatDifference(row.diff) : 'DNF'}</span>
                      <span className="tabular-nums">Time: {formatTime(row.answerTime) || '—'}</span>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}

        {finished ? (
          <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100">Game over. Closest total score wins.</div>
        ) : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Leaderboard</div>
          <div className="mt-3 space-y-2">
            {rankings.map((entry, index) => (
              <div key={entry.socketId} className="flex items-center justify-between rounded-2xl bg-white px-3 py-2 text-sm shadow-sm dark:bg-slate-900">
                <div className="font-medium">#{index + 1} {entry.username}</div>
                <div className="font-semibold text-emerald-700 dark:text-emerald-300">{entry.score}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">How it works</div>
          <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-slate-600 dark:text-slate-300">
            <li>Everyone guesses at the same time.</li>
            <li>Guesses stay hidden until everyone has answered.</li>
            <li>Closest guesses get the best points, with optional near-answer bonuses.</li>
            <li>The highest total after all rounds wins.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

ClosestGuessGame.Guide = function ClosestGuessGuide() {
  return (
    <div>
      <h3 className="mb-2 font-bold">Closest Guess</h3>
      <p>Guess the closest number to each question before the timer runs out. The closest answers score the most points.</p>
    </div>
  );
};
